import { Injectable } from '@angular/core';
import { OrigamiResponse, ServiceBase } from './base/service.base';
import { TmfPublishing, TopicField } from '@app/origami-api/utils/topic-utils';
import { Observable } from 'rxjs';
import { ErrorCode } from '@app/origami-api/constants/error-codes';
import {
  EntityType,
  ICommonDataProtocol,
  ICommonMethodOfDataReceipt,
} from '@app/ti-components/study-start-up/bread-crumbs/models/commonDataProtocol';
import { CountryFilterMode } from '@app/origami-api/constants/country-filter-mode';
import { SessionService } from '@app/session/session.service';
import { map } from 'rxjs/operators';
import { CountriesModel } from '@app/pages/main/pages/startup-countries/models/countries.interfaces';
import { StudyStartUpModel } from '@app/ti-components/study-start-up/models/study-start-up.interfaces';
import TypeTabHeaderSetting = StudyStartUpModel.TypeTabHeaderSetting;
import CountryStatus = CountriesModel.CountryStatus;

export interface Language {
  IsoCode: string;
  LanguageName: string;
}

export interface Country {
  Amendment: any;
  CountryName: string;
  DataRoomTopicFields: TopicField[];
  DomainTopicFields: TopicField[];
  TopicFields?: TopicField[];
  HasRequiredDocuments: boolean;
  IsCTISMember: boolean;
  ParentId: number;
  Iso2: string;
  SiteId: number;
  TopicId: number;
  TopicName: string;
  TopicTypeId: number;
}

export interface GetCountriesOptions {
  siteId: number;
  filterMode: number;
  amendmentProfileId?: number;
  start?: number;
  limit?: number;
  orderByClause?: string;
  orderBy?: string;
  tmfPublishing?: TmfPublishing;
  fieldListing?: any;
  searchQuery?: string;
  filterValue?: string | number;
}

export interface GetCountryOptions {
  siteId: number;
  countryTopicId: number;
}

export interface GetCountriesResult {
  Countries: Country[];
  TotalRowCount: number;
}

export interface GetLanguageList {
  siteId: number;
  isoCode?: string;
  countryCodes?: string[];
  investigativeSiteIds?: number[];
  irbIds?: number[];
  getTranslationLanguages?: number;
}

export interface ModifyCountryOptions {
  country: ModifiedCountryDetails;
  siteId: string | number;
}

export interface ChangeCountryStatusOptions {
  siteId: number;
  countryId: number;
  countryFields: TopicField[];
  status: CountryStatus;
  documentIdsMovingToEtmf: number[];
  completeAmendments?: boolean;
  doNotMoveAnyDocumentsToEtmf: boolean;
}

export interface ModifiedCountryDetails {
  DataRoomTopicFields?: TopicField[];
  DomainTopicFields?: TopicField[];
  SiteId: string | number;
  TopicId?: number;
  TopicName: string;
  TopicTypeId: number;
}

export interface SimpleCountry {
  Iso2: string;
  Iso3: string;
  Name: string;
  code?: string;
  phoneTemplate?: string;
  CountryName?: string;
}

export interface SingleCountry extends Country {
  Iso3: string;
  TopicFields: TopicField[];
}

export interface IResponseCountryByView {
  EntitiesCount: number;
  ViewFilterDisplayName: string;
  ViewFilterId: TypeTabHeaderSetting;
}

export interface CreateCountryOptions {
  siteId: number;
  countries: ModifiedCountryDetails[];
}

export interface DeleteCountryOptions {
  siteId: number;
  countries: {
    ParentId: number;
    SiteId: number;
    TopicId: number;
    TopicName: string;
  }[];
}

export interface SetRMSCountryOptions {
  siteId: number;
  countryIso2: string;
  rmsStatus: number;
  comment: string;
}

@Injectable()
export class CountryService extends ServiceBase implements ICommonMethodOfDataReceipt {
  serviceUrl = 'CountryService.svc';

  getCountry(
    body: GetCountryOptions,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<Country>> {
    return this.origamiPost('/GetCountry', body, options, silentErrorCodes, forceRequest);
  }

  getCountries(body: GetCountriesOptions, ...params): Observable<OrigamiResponse<GetCountriesResult>> {
    return this.origamiPost('/GetCountries', body, ...params);
  }

  getCountryViews(
    siteId: number,
    viewMode: number = 1,
    amendmentProfileId = null,
  ): Observable<OrigamiResponse<IResponseCountryByView[]>> {
    return this.origamiPost(
      '/GetCountryViews',
      {
        siteId,
        viewMode,
        amendmentProfileId,
      },
      null,
      null,
      true,
    );
  }

  getAllCountries(...params): Observable<OrigamiResponse<SimpleCountry[]>> {
    return this.origamiPost('/GetAllCountries', null, ...params);
  }

  getLanguageList(
    body: GetLanguageList,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<Language[]>> {
    return this.origamiPost('/GetLanguageList', body, options, silentErrorCodes, forceRequest);
  }

  modifyCountryMetadata(body: ModifyCountryOptions, ...params): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/ModifyCountry', body, ...params);
  }

  changeCountryStatus(body: ChangeCountryStatusOptions, ...params): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/ChangeCountryStatus', body, ...params);
  }

  getEntity(searchText: string, sessionService: SessionService): Observable<ICommonDataProtocol[]> {
    const request = {
      amendmentProfileId: null,
      siteId: sessionService.room.id,
      filterMode: CountryFilterMode.DataRoomWithEssentialDocuments,
      tmfPublishing: { RetrieveFromTmf: false },
      searchQuery: searchText ? `[$$CountryName$$] like '%${searchText}%'` : '',
    };
    return this.getCountries(request, null, null, null, null).pipe(
      map((response) =>
        response.Result.Countries.map((country) => {
          return {
            name: country.CountryName,
            value: country.Iso2,
            topicId: country.TopicId,
            type: EntityType.Country,
          };
        }),
      ),
    );
  }

  createCountry(
    body: CreateCountryOptions,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/CreateCountry', body, options, silentErrorCodes, forceRequest);
  }

  deleteCountry(
    body: DeleteCountryOptions,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/DeleteCountry', body, options, silentErrorCodes, forceRequest);
  }

  setRMSCountry(
    body: SetRMSCountryOptions,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/SetRMSCountry', body, options, silentErrorCodes, forceRequest);
  }
}
